import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { getTechPartners } from "@/lib/api";

/**
 * Editorial partner marquee — clean transparent PNGs.
 * Wrapper has overflow: visible; only horizontal fade masks the edges.
 */
export default function TechPartnersMarquee() {
  const [data, setData] = useState({ partners: [], categories: [] });
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    getTechPartners().then(setData).catch(() => {});
  }, []);

  const partners = data.partners || [];
  const track = [...partners, ...partners];

  return (
    <section
      ref={ref}
      data-testid="tech-partners"
      className="theme-editorial relative bg-[#FAFAFA] border-y border-[rgba(17,17,17,0.06)] py-20 md:py-24"
      aria-label="Technology partners"
    >
      <div className="mx-auto max-w-[1280px] px-6 md:px-10 mb-12 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-editorial-mono text-[10.5px] text-[#5B2A86] mb-3">
            OUR TECHNOLOGY PARTNERSHIPS
          </div>
          <h2 className="font-editorial-display text-[#111] text-[36px] md:text-[52px] leading-[1.02] tracking-[-0.02em]">
            Best-in-class technology, <span className="font-editorial-italic text-[#5B2A86]">integrated for you.</span>
          </h2>
        </div>
        <p className="max-w-md text-[15px] leading-[1.55] text-[#2A2A2A]">
          We deploy and operate the platforms that lead the Gartner Magic Quadrants — across SOC,
          IDAM, EDR / MDR, SASE, Cloud &amp; Application, and Data Protection.
        </p>
      </div>

      <div className="mx-auto max-w-[1280px] px-6 md:px-10 mb-10 flex flex-wrap gap-x-6 gap-y-3">
        {(data.categories || []).map((c) => (
          <span
            key={c.key}
            className="inline-flex items-center gap-2 font-editorial-mono text-[10.5px] text-[#111]"
            data-testid={`partner-cat-${c.key}`}
          >
            <span className="inline-block h-1 w-1 rounded-full bg-[#5B2A86]" />
            {c.label}
          </span>
        ))}
      </div>

      {/* Marquee — vertical overflow visible so logos don't clip; horizontal fade via mask */}
      <div className="marquee-mask-white relative overflow-x-hidden overflow-y-visible" style={{ paddingBlock: 24 }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-16 md:gap-24 animate-[marquee_60s_linear_infinite] hover:[animation-play-state:paused] w-max"
        >
          {track.map((p, i) => (
            <div
              key={`${p.slug}-${i}`}
              data-testid={`partner-logo-${p.slug}`}
              className="shrink-0 flex items-center justify-center"
              style={{ height: 88, width: 220 }}
              title={p.name}
            >
              <img
                src={p.logo}
                alt={p.name}
                loading="lazy"
                className="transition-all duration-500"
                style={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  objectFit: "contain",
                  objectPosition: "center",
                  filter: "grayscale(1) opacity(0.55)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.filter = "grayscale(0) opacity(1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = "grayscale(1) opacity(0.55)"; }}
              />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
